/**
 * @file Route-scoped Firestore listener ownership for Shared Calendar entries.
 */

import {
    collection,
    limit,
    onSnapshot,
    orderBy,
    query,
    where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db } from "../../app/firebase/firebase.js";
import { state } from "../../app/state/state.js";
import {
    addCalendarMonths,
    getBufferedCalendarMonthKeys,
    getCurrentCalendarMonthKey,
} from "./calendarDateUtils.js";
import {
    resetCalendarState,
    setCalendarEntries,
    setCalendarListenerStatus,
    setCalendarLoadedMonthKeys,
    setCalendarOverflowMonthKeys,
    setCalendarVisibleMonths,
} from "./calendarState.js";

const CALENDAR_ENTRY_MONTH_LIMIT = 500;
const VISIBLE_MONTH_COUNT = 2;
const BUFFER_MONTH_COUNT = 3;

let activeRouteListeners = new Map();
let activeListenerKey = null;
let activeVisibleMonthKey = getCurrentCalendarMonthKey();
let activeMonthKeys = [];
let entriesByMonthKey = new Map();
let entrySlicesByMonthKey = new Map();
let loadedMonthKeys = new Set();
let overflowMonthKeys = new Set();
let failedMonthKeys = new Set();

function mapSnapshotDocs(snapshot) {
    return snapshot.docs
        .map((snapshotDoc) => ({
            id: snapshotDoc.id,
            ...snapshotDoc.data(),
        }));
}

function getCurrentUserId() {
    return state.currentUser?.uid ||
        state.currentUserProfile?.id ||
        state.currentUserProfile?.uid ||
        "";
}

function getMonthStartDate(monthKey) {
    return /^\d{4}-\d{2}$/.test(String(monthKey || "")) ?
        `${monthKey}-01` :
        "";
}

function getMonthEndDate(monthKey) {
    const nextMonthKey = addCalendarMonths(monthKey, 1);
    if (!nextMonthKey) {return "";}
    const nextMonth = new Date(`${nextMonthKey}-01T00:00:00.000Z`);
    nextMonth.setUTCDate(0);
    return nextMonth.toISOString().slice(0, 10);
}

function filterEntriesForMonth(entries = [], monthKey) {
    return (Array.isArray(entries) ? entries : [])
        .filter((entry) => {
            return Array.isArray(entry.monthKeys) &&
                entry.monthKeys.includes(monthKey);
        });
}

function getMonthEntrySlice(monthKey) {
    if (!entrySlicesByMonthKey.has(monthKey)) {
        entrySlicesByMonthKey.set(monthKey, {
            privateEntries: [],
            leaveEntries: [],
            privateLoaded: false,
            leaveLoaded: false,
            privateFailed: false,
            leaveFailed: false,
            privateOverflow: false,
            leaveOverflow: false,
        });
    }
    return entrySlicesByMonthKey.get(monthKey);
}

function recomputeMonthEntries(monthKey) {
    const slice = getMonthEntrySlice(monthKey);
    const entriesById = new Map();
    [
        ...slice.privateEntries,
        ...slice.leaveEntries,
    ].forEach((entry) => {
        entriesById.set(entry.id, entry);
    });
    entriesByMonthKey.set(
        monthKey,
        Array.from(entriesById.values())
            .sort((a, b) => String(a.startDate || "")
                .localeCompare(String(b.startDate || "")))
            .slice(0, CALENDAR_ENTRY_MONTH_LIMIT),
    );
}

function updateMonthSlice(monthKey, type, docs) {
    const slice = getMonthEntrySlice(monthKey);
    const overflow = docs.length > CALENDAR_ENTRY_MONTH_LIMIT;
    if (type === "private") {
        slice.privateEntries = docs.slice(0, CALENDAR_ENTRY_MONTH_LIMIT);
        slice.privateLoaded = true;
        slice.privateFailed = false;
        slice.privateOverflow = overflow;
    } else {
        slice.leaveEntries = docs.slice(0, CALENDAR_ENTRY_MONTH_LIMIT);
        slice.leaveLoaded = true;
        slice.leaveFailed = false;
        slice.leaveOverflow = overflow;
    }

    recomputeMonthEntries(monthKey);
    if (slice.privateFailed || slice.leaveFailed) {
        failedMonthKeys.add(monthKey);
        loadedMonthKeys.delete(monthKey);
    } else if (slice.privateLoaded && slice.leaveLoaded) {
        loadedMonthKeys.add(monthKey);
        failedMonthKeys.delete(monthKey);
    }

    if (slice.privateOverflow || slice.leaveOverflow) {
        overflowMonthKeys.add(monthKey);
    } else {
        overflowMonthKeys.delete(monthKey);
    }
}

function markMonthSliceFailed(monthKey, type) {
    const slice = getMonthEntrySlice(monthKey);
    if (type === "private") {
        slice.privateEntries = [];
        slice.privateLoaded = false;
        slice.privateFailed = true;
        slice.privateOverflow = false;
    } else {
        slice.leaveEntries = [];
        slice.leaveLoaded = false;
        slice.leaveFailed = true;
        slice.leaveOverflow = false;
    }

    recomputeMonthEntries(monthKey);
    loadedMonthKeys.delete(monthKey);
    overflowMonthKeys.delete(monthKey);
    failedMonthKeys.add(monthKey);
}

function isMissingIndexError(error) {
    return String(error?.message || error || "")
        .toLowerCase()
        .includes("requires an index");
}

function mergeMonthEntries(monthKeys) {
    const entriesById = new Map();

    monthKeys.forEach((monthKey) => {
        const entries = entriesByMonthKey.get(monthKey) || [];
        entries.forEach((entry) => {
            entriesById.set(entry.id, entry);
        });
    });

    return Array.from(entriesById.values())
        .sort((a, b) => String(a.startDate || "")
            .localeCompare(String(b.startDate || "")));
}

function updateListenerState(monthKeys) {
    const pendingMonthKeys = monthKeys
        .filter((monthKey) => !loadedMonthKeys.has(monthKey) &&
            !failedMonthKeys.has(monthKey));
    const overflowKeys = monthKeys
        .filter((monthKey) => overflowMonthKeys.has(monthKey));
    const failedKeys = monthKeys
        .filter((monthKey) => failedMonthKeys.has(monthKey));
    const listenerError = failedKeys.length > 0 ?
        new Error(`Calendar entries failed to load for ${failedKeys.join(", ")}.`) :
        null;

    const mergedEntries = mergeMonthEntries(monthKeys);
    setCalendarEntries(mergedEntries);
    setCalendarLoadedMonthKeys(monthKeys);
    setCalendarOverflowMonthKeys(overflowKeys);

    if (failedKeys.length === monthKeys.length) {
        setCalendarListenerStatus("error", listenerError);
    } else if (pendingMonthKeys.length > 0 && loadedMonthKeys.size === 0) {
        setCalendarListenerStatus("loading");
    } else if (failedKeys.length > 0 ||
        overflowKeys.length > 0 ||
        pendingMonthKeys.length > 0) {
        setCalendarListenerStatus("partial", listenerError);
    } else {
        setCalendarListenerStatus("ready");
    }
}

function notifyDataChanged(callback) {
    if (typeof callback === "function") {
        callback();
    }
}

function detachUnusedMonthListeners(monthKeys) {
    const desiredMonthKeys = new Set(monthKeys);
    activeRouteListeners.forEach((unsubscribe, monthKey) => {
        if (desiredMonthKeys.has(monthKey)) {return;}
        try {
            unsubscribe();
        } catch (error) {
            console.warn("Error detaching Calendar month listener:", error);
        }
        activeRouteListeners.delete(monthKey);
        entriesByMonthKey.delete(monthKey);
        entrySlicesByMonthKey.delete(monthKey);
        loadedMonthKeys.delete(monthKey);
        overflowMonthKeys.delete(monthKey);
        failedMonthKeys.delete(monthKey);
    });
}

function attachMonthListener(monthKey, onData) {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
        failedMonthKeys.add(monthKey);
        updateListenerState(activeMonthKeys);
        notifyDataChanged(onData);
        return;
    }

    const monthStartDate = getMonthStartDate(monthKey);
    const monthEndDate = getMonthEndDate(monthKey);
    let privateStartingDocs = [];
    let privateSpanningDocs = [];
    let privateStartingLoaded = false;
    let privateSpanningLoaded = false;

    const syncPrivateEntries = () => {
        if (!privateStartingLoaded || !privateSpanningLoaded) {return;}
        const entriesById = new Map();
        filterEntriesForMonth([
            ...privateStartingDocs,
            ...privateSpanningDocs,
        ], monthKey).forEach((entry) => {
            entriesById.set(entry.id, entry);
        });
        updateMonthSlice(monthKey, "private", Array.from(entriesById.values()));
        updateListenerState(activeMonthKeys);
        notifyDataChanged(onData);
    };

    const privateStartingEntriesQuery = query(
        collection(db, "calendar_entries"),
        where("visibleTo", "array-contains", currentUserId),
        where("startDate", ">=", monthStartDate),
        where("startDate", "<=", monthEndDate),
        orderBy("startDate"),
        limit(CALENDAR_ENTRY_MONTH_LIMIT + 1),
    );
    const privateSpanningEntriesQuery = query(
        collection(db, "calendar_entries"),
        where("visibleTo", "array-contains", currentUserId),
        where("endDate", ">=", monthStartDate),
        orderBy("endDate"),
        limit(CALENDAR_ENTRY_MONTH_LIMIT + 1),
    );
    const leaveEntriesQuery = query(
        collection(db, "calendar_entries"),
        where("sourceType", "==", "leave"),
        where("monthKeys", "array-contains", monthKey),
        orderBy("startDate"),
        limit(CALENDAR_ENTRY_MONTH_LIMIT + 1),
    );

    const privateStartingEntriesListener = onSnapshot(
        privateStartingEntriesQuery,
        (snapshot) => {
            privateStartingDocs = mapSnapshotDocs(snapshot);
            privateStartingLoaded = true;
            syncPrivateEntries();
        },
        (error) => {
            console.error(`Error listening to private starting Calendar entries for ${monthKey}:`, error);
            markMonthSliceFailed(monthKey, "private");
            updateListenerState(activeMonthKeys);
            notifyDataChanged(onData);
        },
    );

    const privateSpanningEntriesListener = onSnapshot(
        privateSpanningEntriesQuery,
        (snapshot) => {
            privateSpanningDocs = mapSnapshotDocs(snapshot)
                .filter((entry) => String(entry.startDate || "") < monthStartDate);
            privateSpanningLoaded = true;
            syncPrivateEntries();
        },
        (error) => {
            if (isMissingIndexError(error)) {
                console.warn(
                    `Spanning Calendar entries index is not ready for ${monthKey}.`,
                    error,
                );
                privateSpanningDocs = [];
                privateSpanningLoaded = true;
                syncPrivateEntries();
                return;
            }
            console.error(`Error listening to private spanning Calendar entries for ${monthKey}:`, error);
            markMonthSliceFailed(monthKey, "private");
            updateListenerState(activeMonthKeys);
            notifyDataChanged(onData);
        },
    );

    const leaveEntriesListener = onSnapshot(
        leaveEntriesQuery,
        (snapshot) => {
            updateMonthSlice(monthKey, "leave", mapSnapshotDocs(snapshot));
            updateListenerState(activeMonthKeys);
            notifyDataChanged(onData);
        },
        (error) => {
            console.error(`Error listening to Leave Calendar entries for ${monthKey}:`, error);
            markMonthSliceFailed(monthKey, "leave");
            updateListenerState(activeMonthKeys);
            notifyDataChanged(onData);
        },
    );
    activeRouteListeners.set(monthKey, () => {
        privateStartingEntriesListener();
        privateSpanningEntriesListener();
        leaveEntriesListener();
    });
}

/**
 * Attaches route-scoped calendar entry listeners for the active month window.
 * @param {object} [options] Listener options.
 * @param {Function} [options.onData] Callback when route data changes.
 * @returns {void}
 */
export function attachCalendarRouteListeners({ onData } = {}) {
    const visibleMonthKey = activeVisibleMonthKey || getCurrentCalendarMonthKey();
    const monthKeys = getBufferedCalendarMonthKeys(
        visibleMonthKey,
        VISIBLE_MONTH_COUNT,
        BUFFER_MONTH_COUNT,
    );
    const currentUserId = getCurrentUserId();
    const nextListenerKey = JSON.stringify({ currentUserId, monthKeys });

    setCalendarVisibleMonths(
        visibleMonthKey,
        addCalendarMonths(visibleMonthKey, VISIBLE_MONTH_COUNT - 1),
    );

    if (activeListenerKey === nextListenerKey) {
        return;
    }

    activeListenerKey = nextListenerKey;
    activeMonthKeys = monthKeys;
    detachUnusedMonthListeners(monthKeys);
    monthKeys.forEach((monthKey) => {
        if (!activeRouteListeners.has(monthKey)) {
            attachMonthListener(monthKey, onData);
        }
    });
    updateListenerState(activeMonthKeys);
    notifyDataChanged(onData);
}

/**
 * Moves the visible Calendar month range and refreshes the buffered listener.
 * @param {number} offset Month offset.
 * @param {object} [options] Listener options.
 * @param {Function} [options.onData] Callback when route data changes.
 * @returns {void}
 */
export function moveCalendarVisibleMonth(offset, { onData } = {}) {
    const nextMonth = addCalendarMonths(activeVisibleMonthKey, offset);
    if (!nextMonth) {return;}
    activeVisibleMonthKey = nextMonth;
    attachCalendarRouteListeners({ onData });
}

/**
 * Moves the visible Calendar month range back to the current month.
 * @param {object} [options] Listener options.
 * @param {Function} [options.onData] Callback when route data changes.
 * @returns {void}
 */
export function showCurrentCalendarMonth({ onData } = {}) {
    activeVisibleMonthKey = getCurrentCalendarMonthKey();
    attachCalendarRouteListeners({ onData });
}

/**
 * Moves the visible Calendar range to a specific month.
 * @param {string} monthKey Month key.
 * @param {object} [options] Listener options.
 * @param {Function} [options.onData] Callback when route data changes.
 * @returns {void}
 */
export function showCalendarMonth(monthKey, { onData } = {}) {
    if (!monthKey) {return;}
    activeVisibleMonthKey = monthKey;
    attachCalendarRouteListeners({ onData });
}

/**
 * Detaches Calendar route listeners and clears route-local Calendar cache.
 * @returns {void}
 */
export function detachCalendarRouteListeners() {
    activeRouteListeners.forEach((unsubscribe) => {
        try {
            unsubscribe();
        } catch (error) {
            console.warn("Error detaching Calendar listener:", error);
        }
    });
    activeRouteListeners = new Map();
    activeListenerKey = null;
    activeMonthKeys = [];
    activeVisibleMonthKey = getCurrentCalendarMonthKey();
    entriesByMonthKey = new Map();
    entrySlicesByMonthKey = new Map();
    loadedMonthKeys = new Set();
    overflowMonthKeys = new Set();
    failedMonthKeys = new Set();
    resetCalendarState();
}
